import { Component } from '@theme/component';
import { DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';

export class SizeGuideDialog extends Component {
  requiredRefs = ['dialog', 'closeButton'];
  #bodyState;
  #opener;
  #backdropPointerDown = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('cancel', this.#onCancel);
    this.refs.dialog.addEventListener('close', this.#onClose);
    this.refs.dialog.addEventListener('pointerdown', this.#onPointerDown);
    this.refs.dialog.addEventListener('click', this.#onClick);
    this.refs.dialog.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    const { dialog } = this.refs;
    dialog.removeEventListener('cancel', this.#onCancel);
    dialog.removeEventListener('close', this.#onClose);
    dialog.removeEventListener('pointerdown', this.#onPointerDown);
    dialog.removeEventListener('click', this.#onClick);
    dialog.removeEventListener('keydown', this.#onKeyDown);
    if (dialog.open) dialog.close();
    this.#restorePage();
    super.disconnectedCallback();
  }

  showDialog(event) {
    const { dialog } = this.refs;
    if (dialog.open || this.#bodyState) return;

    this.#opener = event?.target?.closest?.('[aria-haspopup="dialog"]') || document.activeElement;
    const properties = ['position', 'top', 'left', 'right', 'width'];
    this.#bodyState = {
      scrollX: window.scrollX,
      scrollY: window.scrollY,
      styles: properties.map((name) => [name, document.body.style.getPropertyValue(name), document.body.style.getPropertyPriority(name)]),
    };
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#bodyState.scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    try {
      dialog.showModal();
    } catch (error) {
      this.#restorePage();
      throw error;
    }
    this.#opener?.setAttribute?.('aria-expanded', 'true');
    this.dispatchEvent(new DialogOpenEvent());
  }

  closeDialog = () => {
    if (this.refs.dialog.open) this.refs.dialog.close();
    this.#restorePage();
  };

  #restorePage() {
    if (!this.#bodyState) return;
    const { styles, scrollX, scrollY } = this.#bodyState;
    this.#bodyState = undefined;
    for (const [name, value, priority] of styles) {
      if (value) document.body.style.setProperty(name, value, priority);
      else document.body.style.removeProperty(name);
    }
    window.scrollTo({ left: scrollX, top: scrollY, behavior: 'instant' });
    this.#opener?.setAttribute?.('aria-expanded', 'false');
    if (this.#opener?.isConnected) this.#opener.focus({ preventScroll: true });
    this.#opener = undefined;
    this.#backdropPointerDown = false;
    this.dispatchEvent(new DialogCloseEvent());
  }

  #onCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  #onClose = () => {
    if (!this.refs.dialog.open) this.#restorePage();
  };

  #onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      return;
    }
    if (event.key !== 'Tab') return;
    const controls = Array.from(this.refs.dialog.querySelectorAll('button, a[href], input, select, textarea, [tabindex]')).filter((element) => {
      if (element.disabled || element.tabIndex < 0 || !element.getClientRects().length || getComputedStyle(element).visibility === 'hidden') return false;
      if (element.type !== 'radio' || !element.name) return true;
      const group = Array.from(this.refs.dialog.querySelectorAll('input[type="radio"]')).filter((radio) => radio.name === element.name && !radio.disabled);
      return element === (group.find((radio) => radio.checked) || group[0]);
    });
    if (!controls.length) return;
    const current = controls.indexOf(document.activeElement);
    const next = current < 0 ? (event.shiftKey ? controls.length - 1 : 0) : (current + (event.shiftKey ? -1 : 1) + controls.length) % controls.length;
    event.preventDefault();
    controls[next].focus({ preventScroll: true });
  };

  #isBackdrop(event) {
    const { dialog } = this.refs;
    if (event.target !== dialog) return false;
    const rect = dialog.getBoundingClientRect();
    return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
  }

  #onPointerDown = (event) => {
    this.#backdropPointerDown = this.#isBackdrop(event);
  };

  #onClick = (event) => {
    // Keep interactions in this top-layer dialog from dismissing a parent dialog.
    event.stopPropagation();
    if (this.refs.closeButton.contains(event.target) || (this.#backdropPointerDown && this.#isBackdrop(event))) this.closeDialog();
    this.#backdropPointerDown = false;
  };
}

if (!customElements.get('size-guide-dialog')) customElements.define('size-guide-dialog', SizeGuideDialog);
